import moment from 'moment';
import {
  cardApprovalTableHeaders,
  cardTableHeaders,
  reconciliationReportTableHeaders,
  sensorTransactionsTableHeaders,
  vehicleRecordTableHeaders,
  vehicleUtilizationTableHeaders,
  vehicleWiseFuelConsumptionTableHeaders
} from './tableHeaders';
import { hasConfiguredFuelVolumeUnit } from './volumeUtils';

const toExportColumns = (headers = []) => headers
  .filter((header) => header.id && !header.disable && header.id !== 'action')
  .map((header) => ({ id: header.id, label: header.name }));

const reportColumns = (columns) => columns.map(([id, label, viewId]) => ({
  id,
  label,
  ...(viewId ? { viewId } : {})
}));

export const EXPORT_COLUMNS = {
  cards: toExportColumns(cardTableHeaders),
  cardApprovals: toExportColumns(cardApprovalTableHeaders),
  reconciliation: toExportColumns(reconciliationReportTableHeaders),
  sensorTransactions: toExportColumns(sensorTransactionsTableHeaders),
  vehicleBreakdown: [
    { id: 'id', label: 'SL No' },
    { id: 'vehicle_number', label: 'Vehicle Number' },
    { id: 'type', label: 'Breakdown Type' },
    { id: 'location', label: 'Breakdown Location' },
    { id: 'start_time', label: 'Created On' },
    { id: 'expense', label: 'Repair Cost' },
    { id: 'duration', label: 'Breakdown Duration' },
    { id: 'description', label: 'Description' },
    { id: 'remarks', label: 'Remarks' },
    { id: 'status', label: 'Status' },
    { id: 'end_time', label: 'Completed On' }
  ],
  vehicleRecords: toExportColumns(vehicleRecordTableHeaders),
  mileage: reportColumns([
    ['order_id', 'Order ID'],
    ['start_time', 'Start Time'],
    ['site_name', 'Site Name'],
    ['group_name', 'Group Name'],
    ['registration_num', 'Vehicle Number', 'vehicle_number'],
    ['uid', 'UID'],
    ['fuel_type', 'Fuel Type'],
    ['odometer_entry', 'Odometer Entry'],
    ['previous_odometer_entry', 'Previous Odometer Entry'],
    ['driven', 'Driven'],
    ['quantity', 'Quantity'],
    ['mileage', 'Mileage']
  ]),
  vehicleUtilization: toExportColumns(vehicleUtilizationTableHeaders),
  vehicleWiseFuelConsumption: toExportColumns(vehicleWiseFuelConsumptionTableHeaders),
  vehicleSummary: reportColumns([
    ['vehicle_name', 'Vehicle Name', 'vehicle_reg_num'],
    ['date', 'Date'],
    ['km_travelled', 'KM Travelled'],
    ['fixed_engine_hours', 'Fixed Engine Hours', 'fixed_eng_hours'],
    ['engine_hours', 'Engine Hours', 'eng_hours'],
    ['key_hours', 'Key Hours'],
    ['fuel_consumed', 'Fuel Consumed'],
    ['litre_per_hour', 'Litre Per Hour'],
    ['km_per_litre', 'KM Per Litre'],
    ['fixed_avg_by_oem', 'Fixed Average By OEM'],
    ['opening_fuel_reading', 'Opening Fuel Reading'],
    ['closing_fuel_reading', 'Closing Fuel Reading'],
    ['stolen', 'Stolen'],
    ['filled', 'Filled', 'fms_filled']
  ]),
  groupWiseComparison: [],
  vehicleGroupWise: [
    { id: 'Date', label: 'Date' },
    { id: 'Inward', label: 'Inward' }
  ],
  hsdVsAvgConsumption: [
    { id: 'si_no', label: 'Sl. No', viewId: 'Sl. No' },
    { id: 'vehicle_type', label: 'Type of Vehicle', viewId: 'Type of Vehicle' },
    { id: 'vehicle_no', label: 'Vehicle No', viewId: 'vehicle no' },
    { id: 'hsd_issued', label: 'HSD/MSD Issued', viewId: 'HSD/MSD Issued' },
    { id: 'starting_hour', label: 'Starting Hour', viewId: 'Starting Hour' },
    { id: 'closed_hour', label: 'Closed Hour', viewId: 'Closed Hour' },
    { id: 'total_hour_runned', label: 'Total Hour Runned', viewId: 'Total Hour Runned' },
    { id: 'starting_km', label: 'Starting Km', viewId: 'Starting Km' },
    { id: 'closed_km', label: 'Closed Km', viewId: 'Closed Km' },
    { id: 'total_km_runned', label: 'Total Km Runned', viewId: 'Total Km Runned' },
    { id: 'actual_fuel_consumption', label: 'Actual Fuel Consuption', viewId: 'Actual Fuel Consuption' },
    { id: 'standard_fuel_consumption', label: 'Standard Fuel Consumption', viewId: 'Standard Fuel Consumption' },
    { id: 'remarks', label: 'Remarks', viewId: 'Remarks' }
  ],
  machineryDieselConsumption: [],
  supportCards: [
    { id: 'uid', label: 'Card UID' },
    { id: 'site_name', label: 'Site' },
    { id: 'card_category', label: 'Category' },
    { id: 'active', label: 'Status' },
    { id: 'fill_limit_type', label: 'Fill Limit Type' },
    { id: 'per_filling_limit', label: 'Per Filling Limit' },
    { id: 'per_day_limit', label: 'Per Day Limit' },
    { id: 'limit_quantity', label: 'Limit Quantity' },
    { id: 'vehicle_number', label: 'Vehicle Number' }
  ],
  supportVehicles: [
    { id: 'registration_num', label: 'Registration Number' },
    { id: 'active', label: 'Status' },
    { id: 'model', label: 'Model' },
    { id: 'site_name', label: 'Site Name' },
    { id: 'asset_code', label: 'Asset Code' },
    { id: 'type', label: 'Type' },
    { id: 'vehicle_group', label: 'Group' },
    { id: 'standard_mileage', label: 'Standard Mileage' }
  ],
  supportTransactions: [
    { id: 'id', label: 'Order ID' },
    { id: 'date_time', label: 'Date' },
    { id: 'site_name', label: 'Site' },
    { id: 'vehicle_number', label: 'Vehicle Number' },
    { id: 'quantity', label: 'Quantity' },
    { id: 'amount', label: 'Amount' }
  ],
  supportIssueTransactions: [
    { id: 'order_id', label: 'Order ID' },
    { id: 'start_time', label: 'Start Time' },
    { id: 'site_name', label: 'Site Name' },
    { id: 'asset_type', label: 'Asset Type' },
    { id: 'address', label: 'Address' },
    { id: 'dispenser_name', label: 'Dispenser Name' },
    { id: 'dispenser_tank_name', label: 'Dispenser Tank' },
    { id: 'card_num_1', label: 'Card UID' },
    { id: 'card_num_2', label: 'Card UID 2' },
    { id: 'vehicle_number', label: 'Vehicle Number' },
    { id: 'vehicle_group', label: 'Vehicle Group' },
    { id: 'vehicle_model', label: 'Vehicle Model' },
    { id: 'vehicle_category', label: 'Vehicle Category' },
    { id: 'asset_code', label: 'Asset Code' },
    { id: 'odometer_entry', label: 'Odometer Entry' },
    { id: 'additional_entry', label: 'Additional Entry' },
    { id: 'totalizer_start_reading', label: 'Opening Reading' },
    { id: 'totalizer_end_reading', label: 'Closing Reading' },
    { id: 'nozzle_num', label: 'Nozzle Number' },
    { id: 'opening_stock', label: 'Opening Stock' },
    { id: 'closing_stock', label: 'Closing Stock' },
    { id: 'quantity', label: 'Quantity' }
  ],
  supportInwardTransactions: [
    { id: 'site_name', label: 'Site Name' },
    { id: 'tank_name', label: 'Tank Name' },
    { id: 'supplier', label: 'Supplier' },
    { id: 'invoice_number', label: 'Invoice Number' },
    { id: 'delivery_vehicle_number', label: 'Vehicle Number' },
    { id: 'order_date', label: 'Order Date' },
    { id: 'delivery_date', label: 'Delivery Date' },
    { id: 'quantity', label: 'Quantity' },
    { id: 'shortage_quantity', label: 'Shortage' },
    { id: 'actual_filled', label: 'Actual' },
    { id: 'price_per_litre', label: 'Price Per Litre' },
    { id: 'source', label: 'Source' },
    { id: 'source_provider', label: 'Source Provider' },
    { id: 'source_reference', label: 'Source Reference' }
  ],
  supportExternalTransactions: [
    { id: 'id', label: 'ID' },
    { id: 'site_name', label: 'Site Name' },
    { id: 'dispenser_name', label: 'Dispenser Name' },
    { id: 'vehicle_number', label: 'Vehicle Number' },
    { id: 'date_time', label: 'Date Time' },
    { id: 'odometer_entry', label: 'Odometer' },
    { id: 'quantity', label: 'Quantity' },
    { id: 'unit_price', label: 'Unit Price' },
    { id: 'amount', label: 'Amount' }
  ]
};

const getGallonColumn = (id, label = id) => ({
  id: `${id}_gallons`,
  label: `${label} Gallons`
});

export const getGroupWiseComparisonExportColumns = (dateValue, includeGallons = false) => {
  const selectedDate = moment(dateValue).isValid() ? moment(dateValue) : moment();
  const previousMonth = selectedDate.clone().subtract(1, 'month').format('MMMM');
  const currentMonth = selectedDate.format('MMMM');
  const valueColumns = [
    `${previousMonth} Internal`,
    `${previousMonth} External`,
    `${previousMonth} Total`,
    `${currentMonth} Internal`,
    `${currentMonth} External`,
    `${currentMonth} Total`,
    'Last 24 Hours Internal',
    'Last 24 Hours External',
    'Last 24 Hours Total'
  ];

  return [
    { id: 'equipment', label: 'Equipment', viewId: 'Equipment' },
    ...valueColumns.map((column) => ({ id: column, label: column })),
    ...(includeGallons ? valueColumns.map((column) => getGallonColumn(column)) : [])
  ];
};

export const getMachineryDieselExportColumns = (includeGallons = false) => [
  { id: 'si_no', label: 'Sl. No', viewId: 'Sl No' },
  { id: 'machine_groups', label: 'Machine Groups', viewId: 'Machine Groups' },
  { id: 'vehicle_no', label: 'Vehicle No', viewId: 'Vehicle No' },
  { id: 'daily_details', label: 'Daily Details' },
  {
    id: 'total_monthly_diesel_received_liter',
    label: 'TOTAL MONTHLY DIESEL RECEIVED IN LITER',
    viewId: 'TOTAL MONTHLY DIESEL RECEIVED IN LITER'
  },
  ...(includeGallons ? [{
    id: 'total_monthly_diesel_received_gallon',
    label: 'TOTAL MONTHLY DIESEL RECEIVED IN GALLON',
    viewId: 'TOTAL MONTHLY DIESEL RECEIVED IN GALLON'
  }] : []),
  { id: 'remarks', label: 'REMARKS', viewId: 'REMARKS' }
];

export const getVehicleSummaryExportColumns = (includeGallons = false) => [
  ...EXPORT_COLUMNS.vehicleSummary,
  ...(includeGallons ? [getGallonColumn('filled', 'Filled')] : [])
];

export const getHsdVsAvgExportColumns = (includeGallons = false) => [
  ...EXPORT_COLUMNS.hsdVsAvgConsumption,
  ...(includeGallons ? [
    { id: 'hsd_issued_gallons', label: 'Gallons', viewId: 'Gallons' }
  ] : [])
];

export const getVehicleGroupWiseExportColumns = (vehicleGroups = [], includeGallons = false) => {
  const groupColumns = vehicleGroups
    .map((group) => group?.name || group?.group_name || group?.label)
    .filter(Boolean)
    .flatMap((groupName) => [
      { id: groupName, label: groupName },
      ...(includeGallons ? [{ id: `${groupName} Gallons`, label: `${groupName} Gallons` }] : [])
    ]);

  return [
    { id: 'Date', label: 'Date' },
    ...groupColumns,
    { id: 'Inward', label: 'Inward' },
    ...(includeGallons ? [{ id: 'Inward Gallons', label: 'Inward Gallons' }] : [])
  ];
};

export const getSupportCardExportColumns = (company) => {
  const columns = EXPORT_COLUMNS.supportCards;
  if (!hasConfiguredFuelVolumeUnit(company)) return columns;

  return [
    ...columns,
    { id: 'per_filling_limit_gallons', label: 'Per Filling Limit Gallons' },
    { id: 'per_day_limit_gallons', label: 'Per Day Limit Gallons' },
    { id: 'limit_quantity_gallons', label: 'Limit Quantity Gallons' }
  ];
};

export const getSupportInwardExportColumns = (company) => {
  if (!hasConfiguredFuelVolumeUnit(company)) return EXPORT_COLUMNS.supportInwardTransactions;

  const volumeColumnLabels = {
    quantity: 'Quantity Gallons',
    shortage_quantity: 'Shortage Gallons',
    actual_filled: 'Actual Gallons'
  };

  return EXPORT_COLUMNS.supportInwardTransactions.map((column) => {
    const label = volumeColumnLabels[column.id];
    return label ? { id: column.id, label } : column;
  });
};

export const getSupportIssueExportColumns = (company) => {
  if (!hasConfiguredFuelVolumeUnit(company)) return EXPORT_COLUMNS.supportIssueTransactions;

  const volumeColumnLabels = {
    opening_stock: 'Opening Stock Gallons',
    closing_stock: 'Closing Stock Gallons',
    quantity: 'Quantity Gallons'
  };

  return EXPORT_COLUMNS.supportIssueTransactions.map((column) => {
    const label = volumeColumnLabels[column.id];
    return label ? { id: column.id, label } : column;
  });
};

export const getSupportTransferExportColumns = (company) => {
  if (!hasConfiguredFuelVolumeUnit(company)) return EXPORT_COLUMNS.supportIssueTransactions;

  const volumeColumnLabels = {
    opening_stock: 'Opening Stock Gallons',
    closing_stock: 'Closing Stock Gallons',
    quantity: 'Quantity Gallons'
  };

  return EXPORT_COLUMNS.supportIssueTransactions.map((column) => {
    const label = volumeColumnLabels[column.id];
    return label ? { id: column.id, label } : column;
  });
};

export const getSupportExternalExportColumns = (company) => {
  if (!hasConfiguredFuelVolumeUnit(company)) return EXPORT_COLUMNS.supportExternalTransactions;

  return EXPORT_COLUMNS.supportExternalTransactions.map((column) => (
    column.id === 'quantity'
      ? { id: column.id, label: 'Quantity Gallons' }
      : column
  ));
};

export const getSupportTransactionExportColumns = (type, company) => {
  if (type === 'issue') return getSupportIssueExportColumns(company);
  if (type === 'transfer') return getSupportTransferExportColumns(company);
  if (type === 'inward') return getSupportInwardExportColumns(company);
  if (type === 'external') return getSupportExternalExportColumns(company);
  return EXPORT_COLUMNS.supportTransactions;
};

export const getReportExportColumns = (title, options = {}) => {
  const key = String(title || '').toLowerCase();
  if (key.includes('cardlimit') || key.includes('card limit') || key.includes('cardapproval')) {
    return EXPORT_COLUMNS.cardApprovals;
  }
  if (key.includes('hsdvsavg') || key.includes('avgconsumption')) {
    return getHsdVsAvgExportColumns(options.includeGallons);
  }
  if (key.includes('customisedmileage')) return EXPORT_COLUMNS.hsdVsAvgConsumption;
  if (key.includes('machinerydiesel')) {
    return getMachineryDieselExportColumns(options.includeGallons);
  }
  if (key.includes('sensor')) return EXPORT_COLUMNS.sensorTransactions;
  if (key.includes('breakdown')) return EXPORT_COLUMNS.vehicleBreakdown;
  if (key.includes('reconciliation')) return EXPORT_COLUMNS.reconciliation;
  if (key.includes('vehiclewisefuel')) return EXPORT_COLUMNS.vehicleWiseFuelConsumption;
  if (key.includes('vehiclesummary')) return getVehicleSummaryExportColumns(options.includeGallons);
  if (key.includes('groupwisecomparison')) {
    return getGroupWiseComparisonExportColumns(options.dateValue, options.includeGallons);
  }
  if (key.includes('vehiclegroupwise')) {
    return getVehicleGroupWiseExportColumns(options.vehicleGroups, options.includeGallons);
  }
  if (key.includes('utilization')) return EXPORT_COLUMNS.vehicleUtilization;
  if (key.includes('vehiclerecord')) return EXPORT_COLUMNS.vehicleRecords;
  if (key.includes('mileage')) return EXPORT_COLUMNS.mileage;
  return EXPORT_COLUMNS.mileage;
};
